import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Pagination, Spin, Empty } from 'antd';
import ajax from '../../utils/ajax';
import { mockData } from './mock';

import 'antd/es/pagination/style/index.css';
import 'antd/es/spin/style/index.css';
import 'antd/es/empty/style/index.css';
import './index.less';

/**
 * 通用职位列表组件
 * @param {Object} props - 组件属性
 * @param {Object} props.searchParams - 搜索参数配置
 * @param {string} props.jobKind - 职位类型：'2'=仅社招，'6'=仅校招，'all'=社招+校招，默认='2'(仅社招)
 * @param {number} props.pageSize - 每页显示条数，默认20
 * @param {string} props.emptyDescription - 空数据提示文字
 * @param {boolean} props.showPagination - 是否显示分页器，默认true
 * @param {string} props.jobSearchTag - 职位搜索标签
 * @param {string} props.compSearchTag - 企业搜索标签
 * @param {boolean} props.development - 是否为开发模式，默认false
 * @param {boolean} props.showOnlineTime - 是否显示在线时间，默认false
 * @param {boolean} props.showLogo - 是否显示公司logo，默认true
 * @param {string} props.componentName - 组件名称，用于埋点
 * @param {string} props.uniqueId - 唯一标识，用于埋点
 * @returns {JSX.Element} 职位列表组件
 */
function JobList({
	searchParams = {},
	jobKind = '6',
	pageSize = 20,
	emptyDescription = '暂无职位信息',
	showPagination = true,
	jobSearchTag,
	compSearchTag,
	development = false,
	showOnlineTime = false,
	showLogo = true,
	componentName = 'jobList',
	uniqueId = 'default',
}) {
	// 状态管理
	const [currentPage, setCurrentPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const [jobData, setJobData] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const useRefSearchParams = useRef({});
	const searchParamsRef = useRef(searchParams);

	/**
	 * 职位数据获取函数
	 * @param {Object} params - 额外的请求参数
	 * @param {number} page - 页码
	 * @returns {Promise<Object>} 职位数据
	 */
	const fetchJobData = useCallback(
		async (params = {}, page = 1) => {
			const currentSearchParams = searchParamsRef.current;
			const {
				city,
				workYearCode,
				salaryCode,
				key,
				industry,
				eduLevel,
				jobTitle,
			} = currentSearchParams;

			try {
				const requestData = {
					curPage: page - 1,
					pageSize,
					sortType: '1',
					jobAudiences: ['1', '2', '3'],
					searchType: '2',
					jobSourceVos: [{ jobKind: '6' },{ jobKind: '2' }],
					dqCode: city ? [city] : [],
					industry: industry || [],
					jobTitle: jobTitle ? [jobTitle] : [],
					keyword: key || '',
					userMatch: false,
					publisherTypes: [],
					workYear: workYearCode ? [workYearCode] : [],
					eduLevel: eduLevel ? [eduLevel] : [],
					showConditionVo: {
						showJobDetail: true,
						showOnlineTime: false,
						tagShowCondition: {
							showCompanyScale: true,
							showCompanyStage: true,
							showCompanyIndustry: true,
							showCompanyKind: true,
							showWorkYear: true,
							showEduLevel: true,
							showJobTags: true,
						},
					},
					compScales: [],
					compKinds: [],
					compTags: [],
				};

				// 添加搜索标签 - 必需字段
				if (jobSearchTag) {
					requestData.jobSearchTag = jobSearchTag;
				} else if (compSearchTag) {
					requestData.compSearchTag = compSearchTag;
				} else {
					// 如果没有提供搜索标签，使用默认值
					requestData.jobSearchTag = "ebfee63e9452481e0ba177c679c3691d";
				}



				const response = await ajax({
					method: 'POST',
					url: 'https://api-wanda.liepin.com/api/com.liepin.rivers.newjob.get-search-jobs',
					data: {
						searchConditionVo: JSON.stringify(requestData),
					},
				});

			if (response.flag === 1) {
				const {
					data: { list: data, totalCount },
				} = response;
				let jobList = data?.length ? data : [];

				// 根据 jobSearchTag 动态添加 mock 数据
				let mockJobCount = 0;
				if (jobSearchTag && mockData[jobSearchTag]) {
					const mockJobs = mockData[jobSearchTag];
					jobList = [...jobList,...mockJobs];
					mockJobCount = mockJobs.length;
				}

				// 返回符合JobList组件预期的数据结构
				return {
					flag: 1,
					data: {
						data: {
							jobCardList: jobList.map((item) => ({
								job: {
									jobId: item.jobId,
									title: item.title,
									dq: item.jobAddress,
									salary: item.salary,
									salaryMonth: '',
									refreshTime: '',
									requireWorkYears: '',
									requireEduLevel: '',
									link: item.pcOuterLink,
                                    jobTags: item.jobTags,
                                    companyName: item.companyName,
                                    compLogo: `https://image0.lietou-static.com/bg_white_192x192/${item.compLogo}`,
								}
							})),
						},
						pagination: {
							pageSize: pageSize,
							totalPage: Math.ceil((totalCount + mockJobCount) / pageSize),
						},
					},
				};
			}

				return {
					flag: 1,
					data: {
						data: { jobCardList: [] },
						pagination: { pageSize: pageSize, totalPage: 0 },
					},
				};
			} catch (error) {
				console.error('获取职位数据失败:', error);
				return {
					flag: 1,
					data: {
						data: { jobCardList: [] },
						pagination: { pageSize: pageSize, totalPage: 0 },
					},
				};
			}
		},
		[pageSize, jobSearchTag, compSearchTag, jobKind]
	);

	/**
	 * 请求职位数据
	 * @param {Object} params - 额外的请求参数
	 * @param {number} page - 页码
	 */
	const loadJobData = useCallback(
		async (params = {}, page = currentPage) => {
			// 检查searchParams是否发生变化
			const currentSearchParams = searchParamsRef.current;
			const searchParamsChanged = JSON.stringify(useRefSearchParams.current) !== JSON.stringify(currentSearchParams);
			
			if (searchParamsChanged) {
				useRefSearchParams.current = currentSearchParams;
				setCurrentPage(1);
				page = 1;
			}

			setLoading(true);

			try {
				// 使用统一的职位接口
				const response = await fetchJobData(params, page);

				if (response.flag === 1) {
					const { data } = response;
					const jobList = data.data.jobCardList || [];
console.log("jobList",jobList)
					// 职位数据转换为JobCard组件需要的格式
					const transformedJobs = jobList.map((item) => ({
						jobId: item.job.jobId,
						jobKind: '2', // 默认社招
						jobTitle: item.job.title,
						title: item.job.title,
						salary: item.job.salary,
						companyName: item.job.companyName,
						compLogo:item.job.compLogo,
						jobTags: item.job.jobTags||[],
						onlineTimeDesc: '', // 该接口暂无在线时间字段
						pcOuterLink: item.job.link,
						traceId: '', // 该接口暂无traceId字段
						// 保留原有字段用于兼容
						id: item.job.jobId,
						jobName: item.job.title,
						location: item.job.dq,
						position: '职位',
						publishTime: '',
						category: 1,
						annualSalary: item.job.salary,
						company: item.job.companyName,
						workYears: '',
						education: '',
						link: item.job.link,
					}));

					setJobData(transformedJobs);
					setTotalCount(
						Number(data.pagination.pageSize) *
							Number(data.pagination.totalPage) || 0
					);
				}
			} catch (error) {
				console.error('获取职位数据失败:', error);
				setJobData([]);
				setTotalCount(0);
			} finally {
				setLoading(false);
			}
		},
		[currentPage, fetchJobData]
	);

	/**
	 * 处理分页变化
	 * @param {number} page - 页码
	 * @param {number} size - 每页大小
	 */
	const handlePageChange = (page, size) => {
		setCurrentPage(page);
	};

	// 监听页码变化，触发数据请求
	useEffect(() => {
		loadJobData({}, currentPage);
	}, [currentPage, loadJobData]);

	// 监听searchParams变化，更新ref
	useEffect(() => {
		searchParamsRef.current = searchParams;
	}, [searchParams]);

	return (
		<div className="job-list-container">
			<Spin spinning={loading} tip="加载中...">
				{jobData.length > 0 ? (
					<div className="job-table-container">
						<table className="job-table">
							<thead>
								<tr>
									<th>招聘职位名称</th>
									<th>工作地点</th>
									<th>职位年薪</th>
								</tr>
							</thead>
							<tbody>
								{jobData.map((item, index) => {
									const handleJobClick = () => {
										if (development) {
											return;
										}
										
										const wwwRoot = "https://www.liepin.com";
										const { jobId, jobKind, traceId, pcOuterLink } = item;
										
										const opts = {
											elemId: `rivers_job_${componentName}_${uniqueId}`,
											cid: `${jobKind}_${jobId}`,
											ctype: 1,
											traceId: traceId
										}
										const pgRef = window.tlog?.getPgRef(opts)
										let link = "";
										
										switch (jobKind) {
											case "1":
												link = `${wwwRoot}/a/${jobId}.shtml?d_sfrom=rivers_job_activity&d_headId=${traceId}&pgRef=${pgRef}`;
												break;
											case "2":
												link = `${wwwRoot}/job/19${jobId}.shtml?d_sfrom=rivers_job_activity&d_headId=${traceId}&pgRef=${pgRef}`;
												break;
											case "5":
												link = `${wwwRoot}/rpojob/${jobId}/?d_sfrom=rivers_job_activity&d_headId=${traceId}&pgRef=${pgRef}`;
												break;
											case "6":
												link = `${wwwRoot}/lptjob/${jobId}/?d_sfrom=rivers_job_activity&d_headId=${traceId}&job_id=${jobId}&job_kind=${jobKind}&pgRef=${pgRef}`;
												break;
											case "7":
												link = `${wwwRoot}/atsjob/${jobId}.shtml?d_sfrom=rivers_job_activity&d_headId=${traceId}&job_id=${jobId}&job_kind=${jobKind}&pgRef=${pgRef}`;
												break;
											default:
												break;
										}
										
										const finalLink = link || (pcOuterLink + 
											(pcOuterLink.indexOf("?") > -1
												? `&d_sfrom=rivers_job_activity&d_headId=${traceId}&pgRef=${pgRef}`
												: `?d_sfrom=rivers_job_activity&d_headId=${traceId}&pgRef=${pgRef}`));
										
										window.open(finalLink, '_blank');
									};
									
									return (
										<tr 
											key={item.jobId} 
											className="job-table-row"
											onClick={handleJobClick}
											data-tlg-elem-id={`rivers_job_${componentName}_${uniqueId}`} 
											data-tlg-scm={`cid=${item.jobKind}_${item.jobId}&ctype=1&traceId=${item.traceId}`} 
											data-tlg-ext={`index=${index}`}
										>
											<td className="job-title-cell">{item.jobTitle || item.title}</td>
											<td className="job-location-cell">{item.location || item.dq || '待定'}</td>
											<td className="job-salary-cell">{item.salary || item.annualSalary}</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				) : (
					<Empty description={emptyDescription} style={{ margin: '40px 0' }} />
				)}
			</Spin>

			{/* 分页器 */}
			{showPagination && totalCount > 0 && (
				<div className="job-list-pagination">
					{/*<Pagination*/}
					{/*	current={currentPage}*/}
					{/*	total={totalCount}*/}
					{/*	pageSize={pageSize}*/}
					{/*	onChange={handlePageChange}*/}
					{/*	showSizeChanger={false}*/}
					{/*/>*/}
				</div>
			)}
		</div>
	);
}

export default JobList;

